import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

type SignupFormData = {
  inviteToken: string;
  email: string;
  password1: string;
  password2: string;
};

export const SignupForm: React.FC = () => {
  const [apiError, setApiError] = useState<string>('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const navigate = useNavigate();
  const location = useLocation();
  const { checkAuth } = useAuth();

  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm<SignupFormData>();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tokenFromUrl = searchParams.get('itoken');

    if (tokenFromUrl) {
      setValue('inviteToken', tokenFromUrl);
    }
  }, [location.search, setValue]);

  const onSubmit = async (data: SignupFormData) => {
    try {
      setFieldErrors({});
      setApiError('');

      const response = await fetch('/api/auth/signup/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': document.cookie.split('csrftoken=')[1]?.split(';')[0] || '',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          email: data.email,
          password1: data.password1,
          password2: data.password2,
          inviteToken: data.inviteToken
        })
      });

      const result = await response.json();

      if (response.ok) {
        await checkAuth();
        navigate('/demo-tour');
      } else {
        if (result.error) {
          setApiError(result.error);
        } else if (result.field_errors) {
          setFieldErrors(result.field_errors);
        } else {
          setApiError('Signup failed. Please check your information and try again.');
        }
      }
    } catch (error) {
      setApiError('Signup failed. Please check your information and try again.');
    }
  };

  return (
    <div className="scientia-card__content">
      <h1 className="scientia-card__title">Create Account</h1>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="field">
          <label className="form-label" htmlFor="inviteToken">Invite Token</label>
          <div className="form-control">
            <input
              type="text"
              id="inviteToken"
              className={`input ${errors.inviteToken || fieldErrors.inviteToken ? 'is-danger' : ''}`}
              autoComplete="off"
              {...register('inviteToken', { required: 'Invite token is required' })}
            />
          </div>
          {(errors.inviteToken || fieldErrors.inviteToken) && (
            <p className="help is-danger">
              {errors.inviteToken?.message || fieldErrors.inviteToken}
            </p>
          )}
        </div>

        <div className="field">
          <label className="form-label" htmlFor="email">Email</label>
          <div className="form-control">
            <input
              type="email"
              id="email"
              className={`input ${errors.email || fieldErrors.email ? 'is-danger' : ''}`}
              autoComplete="email"
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address'
                }
              })}
            />
          </div>
          {(errors.email || fieldErrors.email) && (
            <p className="form-error">
              {errors.email?.message || fieldErrors.email}
            </p>
          )}
        </div>

        <div className="field">
          <label className="form-label" htmlFor="password1">Password</label>
          <div className="form-control">
            <input
              type="password"
              id="password1"
              autoComplete="new-password"
              className={`input ${errors.password1 || fieldErrors.password1 ? 'is-danger' : ''}`}
              {...register('password1', {
                required: 'Password is required',
                minLength: {
                  value: 8,
                  message: 'Password must be at least 8 characters'
                }
              })}
            />
          </div>
          {(errors.password1 || fieldErrors.password1) && (
            <p className="form-error">
              {errors.password1?.message || fieldErrors.password1}
            </p>
          )}
        </div>

        <div className="field">
          <label className="form-label" htmlFor="password2">Confirm Password</label>
          <div className="form-control">
            <input
              type="password"
              id="password2"
              autoComplete="new-password"
              className={`input ${errors.password2 || fieldErrors.password2 ? 'is-danger' : ''}`}
              {...register('password2', {
                required: 'Please confirm your password',
                validate: (value) => value === watch('password1') || 'Passwords do not match'
              })}
            />
          </div>
          {(errors.password2 || fieldErrors.password2) && (
            <p className="form-error">
              {errors.password2?.message || fieldErrors.password2}
            </p>
          )}
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="button is-primary is-fullwidth"
          >
            Sign Up
          </button>
        </div>

        {apiError && (
          <div className="form-message is-error" role="alert">
            {apiError}
          </div>
        )}
      </form>

      <div className="has-text-centered mt-4">
        <a href="/auth/login" className="has-text-primary">
          Already have an account? Sign in
        </a>
      </div>
    </div>
  );
};
